import { useState, useEffect } from 'react';
import api from '../../api';

const ACTION_CONFIG = {
  clock_in: { emoji: '🟢', label: 'Clock In', color: 'var(--success)' },
  clock_out: { emoji: '🔴', label: 'Clock Out', color: 'var(--danger)' },
  pay_staff: { emoji: '💵', label: 'Staff Paid', color: 'var(--accent-secondary)' },
  pay_all_staff: { emoji: '💰', label: 'All Staff Paid', color: 'var(--accent-secondary)' },
  bulk_bonus: { emoji: '🎁', label: 'Bulk Bonus', color: '#9B59B6' },
  holiday_bonus: { emoji: '🎊', label: 'Holiday Bonus', color: '#9B59B6' },
  grant_bonus: { emoji: '🎁', label: 'Bonus Granted', color: '#9B59B6' },
  adjust_time: { emoji: '⚖️', label: 'Time Adjusted', color: 'var(--warning)' },
  approve_adjustment: { emoji: '✅', label: 'Adjustment Approved', color: 'var(--success)' },
  deny_adjustment: { emoji: '❌', label: 'Adjustment Denied', color: 'var(--danger)' },
  add_staff: { emoji: '👤', label: 'Staff Added', color: 'var(--info)' },
  remove_staff: { emoji: '👋', label: 'Staff Removed', color: 'var(--danger)' },
  set_rank: { emoji: '🏷️', label: 'Rank Changed', color: 'var(--info)' },
  set_status: { emoji: '📝', label: 'Status Changed', color: 'var(--info)' },
  period_reset: { emoji: '🔄', label: 'Period Reset', color: 'var(--accent-primary)' },
};

const ACTION_FILTERS = ['all', 'clock_in', 'clock_out', 'pay_staff', 'pay_all_staff', 'bulk_bonus', 'holiday_bonus', 'adjust_time', 'add_staff', 'remove_staff', 'period_reset'];

export default function LogsPage({ guild }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const PER_PAGE = 30;

  useEffect(() => {
    api.get(`/api/logs/${guild.id}?limit=500`)
      .then(res => setLogs(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [guild.id]);

  const filtered = logs.filter(l => {
    const matchAction = filterAction === 'all' || l.action === filterAction;
    const matchSearch = !search ||
      (l.actor_username || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.target_discord_id || '').includes(search) ||
      (l.action || '').includes(search.toLowerCase());
    return matchAction && matchSearch;
  });

  const paginated = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>Loading logs...
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Audit Logs</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>{logs.length} total actions logged</p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search by user, action..."
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          style={{
            flex: 1, minWidth: '200px', padding: '10px 16px',
            border: '2px solid var(--border)', borderRadius: '10px',
            fontSize: '14px', fontFamily: 'var(--font-body)',
            background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
        <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(0); }}
          style={{
            padding: '10px 16px', border: '2px solid var(--border)', borderRadius: '10px',
            fontSize: '14px', fontFamily: 'var(--font-body)',
            background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
          }}>
          {ACTION_FILTERS.map(a => (
            <option key={a} value={a}>
              {a === 'all' ? 'All Actions' : ACTION_CONFIG[a]?.label || a}
            </option>
          ))}
        </select>
      </div>

      {/* Logs table */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '40px 1.5fr 1.5fr 1fr 2fr',
          padding: '12px 20px', borderBottom: '1px solid var(--border)',
          fontSize: '12px', fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: '0.5px', color: 'var(--text-muted)',
        }}>
          <span></span><span>Action</span><span>By</span><span>Target</span><span>Details</span>
        </div>

        {paginated.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>No logs found.</div>
        ) : paginated.map(log => {
          const cfg = ACTION_CONFIG[log.action] || { emoji: '📋', label: log.action, color: 'var(--text-muted)' };
          let detailText = '';
          if (log.details) {
            try {
              const d = typeof log.details === 'string' ? JSON.parse(log.details) : log.details;
              if (d.amount) detailText += `Amount: ${d.amount}`;
              if (d.reason) detailText += ` • ${d.reason}`;
              if (d.recipient_count) detailText += ` • ${d.recipient_count} recipients`;
              if (d.note) detailText += ` • ${d.note}`;
            } catch {}
          }

          return (
            <div key={log.id}>
              <div style={{
                display: 'grid', gridTemplateColumns: '40px 1.5fr 1.5fr 1fr 2fr',
                padding: '12px 20px', borderBottom: expanded === log.id ? 'none' : '1px solid var(--border)',
                alignItems: 'center', transition: 'background 0.15s', cursor: 'pointer',
                background: expanded === log.id ? 'var(--accent-soft)' : 'transparent',
              }}
                onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                onMouseEnter={e => { if (expanded !== log.id) e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                onMouseLeave={e => { if (expanded !== log.id) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: '18px' }}>{cfg.emoji}</span>
                <div>
                  <span style={{
                    fontSize: '12px', fontWeight: 700, padding: '3px 8px', borderRadius: '100px',
                    background: cfg.color + '20', color: cfg.color,
                  }}>{cfg.label}</span>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    {new Date(log.created_at).toLocaleString()}
                  </div>
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                  {log.actor_username || log.actor_discord_id || '—'}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {log.target_discord_id || '—'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{detailText || '—'}</span>
                  <span style={{ fontSize: '10px', flexShrink: 0 }}>{expanded === log.id ? '▲' : '▼'}</span>
                </div>
              </div>
              {expanded === log.id && (
                <div style={{
                  padding: '14px 20px 16px 68px', background: 'var(--accent-soft)',
                  borderBottom: '1px solid var(--border)', fontSize: '13px',
                }}>
                  <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>Full Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '6px 16px', color: 'var(--text-secondary)' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>Log ID</span>
                    <span>#{log.id}</span>
                    <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>Action</span>
                    <span>{log.action}</span>
                    <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>Actor ID</span>
                    <span>{log.actor_discord_id || '—'}</span>
                    <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>Target ID</span>
                    <span>{log.target_discord_id || '—'}</span>
                    <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>Timestamp</span>
                    <span>{new Date(log.created_at).toLocaleString()}</span>
                    {log.details && (
                      <>
                        <span style={{ fontWeight: 600, color: 'var(--text-muted)' }}>Raw Details</span>
                        <pre style={{ margin: 0, fontSize: '12px', background: 'var(--bg-card)', padding: '8px', borderRadius: '6px', overflow: 'auto', maxWidth: '400px', color: 'var(--text-primary)' }}>
                          {JSON.stringify(typeof log.details === 'string' ? JSON.parse(log.details) : log.details, null, 2)}
                        </pre>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '20px' }}>
          <button onClick={() => setPage(0)} disabled={page === 0}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === 0 ? 'not-allowed' : 'pointer', color: 'var(--text-secondary)', opacity: page === 0 ? 0.5 : 1 }}>«</button>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === 0 ? 'not-allowed' : 'pointer', color: 'var(--text-secondary)', opacity: page === 0 ? 0.5 : 1 }}>← Prev</button>
          <span style={{ fontSize: '14px', color: 'var(--text-secondary)', padding: '0 12px' }}>
            Page {page + 1} of {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1}
            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer', color: 'var(--text-secondary)', opacity: page === totalPages - 1 ? 0.5 : 1 }}>Next →</button>
          <button onClick={() => setPage(totalPages - 1)} disabled={page === totalPages - 1}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === totalPages - 1 ? 'not-allowed' : 'pointer', color: 'var(--text-secondary)', opacity: page === totalPages - 1 ? 0.5 : 1 }}>»</button>
        </div>
      )}
    </div>
  );
}
